import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import api from '../lib/api';
import { useTrial } from '../contexts/TrialContext';
import { Clock, FileText, CheckCircle, Lock, ArrowRight, Headphones, BookOpen, PenTool, Mic, Loader2 } from 'lucide-react';

const PAGE_SIZE = 8;
const SKILL_ICON = { listening: Headphones, reading: BookOpen, writing: PenTool, speaking: Mic };
const SKILL_LABEL = { listening: 'Listening', reading: 'Reading', writing: 'Writing', speaking: 'Speaking' };

function AttemptStatus({ exam }) {
  const la = exam.last_attempt;
  if (la?.status === 'completed') {
    return (
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 text-sm text-green-600" data-testid={`attempt-status-${exam.exam_number}`}><CheckCircle size={14} />Completed — CLB {la.overall_score}</div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Link to={`/exams/${exam.id}/results/${la.id}`} className="flex-1 sm:flex-none text-center px-3 py-2 sm:py-1.5 text-sm border border-neutral-300 rounded-lg hover:bg-neutral-50 transition" data-testid={`view-results-${exam.exam_number}`}>Results</Link>
          <Link to={`/exams/${exam.id}/take`} className="flex-1 sm:flex-none text-center px-3 py-2 sm:py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-700 transition" data-testid={`retake-exam-${exam.exam_number}`}>Retake</Link>
        </div>
      </div>
    );
  }
  if (la?.status === 'in_progress') {
    return (
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-semibold text-amber-700" data-testid={`attempt-status-${exam.exam_number}`}>In progress · started {new Date(la.started_at || la.created_at).toLocaleDateString()}</span>
        <Link to={`/exams/${exam.id}`} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition text-sm font-medium" data-testid={`resume-exam-${exam.exam_number}`}>
          Continue Exam <ArrowRight size={16} />
        </Link>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <span className="text-xs text-neutral-400" data-testid={`attempt-status-${exam.exam_number}`}>Not started</span>
      <Link to={`/exams/${exam.id}`} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition text-sm font-medium" data-testid={`start-exam-${exam.exam_number}`}>
        Start Exam <ArrowRight size={16} />
      </Link>
    </div>
  );
}

function ExamCard({ exam }) {
  const { entryLabel } = useTrial();
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 hover:border-primary/30 hover:shadow-sm transition flex flex-col" data-testid={`exam-card-${exam.exam_number}`}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h3 className="font-semibold text-neutral-900 text-[15px] sm:text-base">{exam.name}</h3>
        {exam.is_free ? <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">{entryLabel}</span>
          : exam.unlocked ? <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full" data-testid={`exam-unlocked-${exam.exam_number}`}>Unlocked for you</span>
          : <span className="px-2 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-600 rounded-full flex items-center gap-1"><Lock size={10} />Pro</span>}
      </div>
      {exam.description && <p className="text-xs sm:text-sm text-neutral-500 line-clamp-2">{exam.description}</p>}
      <div className="flex items-center gap-1.5 mt-2 sm:mt-3 flex-wrap" data-testid={`exam-skills-${exam.exam_number}`}>
        {(exam.skills || []).map((s) => { const I = SKILL_ICON[s]; return <span key={s} className="inline-flex items-center gap-1 rounded-full bg-neutral-50 border border-neutral-200 px-2 py-0.5 text-[11px] font-medium text-neutral-600">{I && <I size={11} />}{SKILL_LABEL[s] || s}</span>; })}
      </div>
      <div className="flex items-center gap-4 text-xs sm:text-sm text-neutral-500 my-3 sm:my-4">
        <span className="flex items-center gap-1"><FileText size={14} />{exam.total_questions} questions</span>
        <span className="flex items-center gap-1"><Clock size={14} />{exam.estimated_duration_minutes || 180} min</span>
      </div>
      <div className="mt-auto"><AttemptStatus exam={exam} /></div>
    </div>
  );
}

export default function ExamList() {
  const [exams, setExams] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const sentinel = useRef(null);
  const hasMore = total !== null && exams.length < total;

  const fetchPage = useCallback(async (offset) => {
    const r = await api.get('/api/exams', { params: { limit: PAGE_SIZE, offset } });
    const count = parseInt(r.headers['x-total-count'] ?? '', 10);
    setTotal(Number.isFinite(count) ? count : offset + r.data.length);
    setExams((prev) => (offset === 0 ? r.data : [...prev, ...r.data.filter((e) => !prev.some((p) => p.id === e.id))]));
  }, []);

  useEffect(() => {
    fetchPage(0).catch(() => setError('Could not load exams — please refresh.')).finally(() => setLoading(false));
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    fetchPage(exams.length).catch(() => {}).finally(() => setLoadingMore(false));
  }, [loadingMore, hasMore, fetchPage, exams.length]);

  useEffect(() => {
    if (!hasMore || !sentinel.current) return undefined;
    const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) loadMore(); }, { rootMargin: '200px' });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  return (
    <DashboardLayout title="Mock Exams">
      <div data-testid="exam-list-page">
        <p className="text-sm sm:text-base text-neutral-600 mb-4 sm:mb-6">Take a full CELPIP mock exam in the exact test format. Each exam covers all 4 skills.</p>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-3 sm:gap-4" data-testid="exam-list-skeleton">{[1, 2, 3, 4].map((i) => <div key={i} className="h-40 sm:h-48 bg-neutral-100 rounded-xl animate-pulse" />)}</div>
        ) : error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700" data-testid="exam-list-error">{error}</div>
        ) : exams.length === 0 ? (
          <div className="rounded-xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-400" data-testid="exam-list-empty">No mock exams are published yet.</div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
              {exams.map((exam) => <ExamCard key={exam.id} exam={exam} />)}
            </div>
            <div ref={sentinel} className="flex flex-col items-center justify-center gap-2 py-6 text-sm text-neutral-400" data-testid="exam-list-footer">
              {loadingMore ? <span className="inline-flex items-center gap-2" data-testid="exam-list-loading-more"><Loader2 size={15} className="animate-spin" /> Loading more…</span>
                : hasMore ? <button type="button" onClick={loadMore} className="px-4 py-2 rounded-lg border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 font-medium" data-testid="exam-list-load-more">Load more ({total - exams.length} left)</button>
                  : <span data-testid="exam-list-count">Showing all {exams.length} mock exam{exams.length === 1 ? '' : 's'}</span>}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
